import { useEffect } from 'react';
import { GetDataForThanksForRSVPQuery } from '@graphql/generated';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useGenerateGiftNote } from '@shared/components';
import { useTranslation } from '@shared/core';
import { useThanksForRSVPTelemetry } from '@apps/thanksForRsvp/ThanksForRSVP.telemetry';

export const useBasicNoteController = (
  setShowPreview: React.Dispatch<React.SetStateAction<boolean>>,
  setMessage: React.Dispatch<React.SetStateAction<string>>,
  eventHandle: string,
  queryData?: GetDataForThanksForRSVPQuery
) => {
  const { previewYourNoteClicked, basicCongratsOfferViewed, giftWrapMessageEdited, giftWrapPromptSent } = useThanksForRSVPTelemetry();

  useEffect(() => {
    basicCongratsOfferViewed({ eventHandle });
  }, [basicCongratsOfferViewed, eventHandle]);

  const { getValueString } = useQueryParamHelper();
  const guestNames = getValueString('guestNames');
  const { t } = useTranslation('thanksForRSVP');
  const tCongratulationsCard = t('congratulationsCard');
  const isPlural = (guestNames?.split(',') ?? []).length > 1;
  const rsvp = getValueString('rsvp');
  const firstPartner = queryData?.eventByName?.info?.ownerFirstName || '';
  const secondPartner = queryData?.eventByName?.info?.fianceeFirstName || '';
  const partners = `${firstPartner}${secondPartner ? ` and ${secondPartner}` : ''}` || tCongratulationsCard.theCouple();
  const prompt = `Simple first-person${isPlural ? ' plural' : ''} message starting with a salutation and congratulating ${partners} on their upcoming wedding in US english.${
    rsvp === 'all' ? ' Say that you are looking forward to attending the wedding.' : rsvp === 'none' ? ' Say that you are sorry to miss the wedding.' : ''
  } Message should finish with a signoff from ${guestNames}.`;
  const promptFallback = `Dear ${partners},

  Congratulations on your wedding!
      
  Wishing you a lifetime of love, happiness, and amazing adventures. Cheers!

  With love,
  ${guestNames}`;

  const { originalNote, isGeneratedMessageLoading } = useGenerateGiftNote({
    prompt,
    fallback: promptFallback,
    onCompleted: (note, _) => {
      setMessage(note);
      giftWrapPromptSent({ prompt });
    }
  });

  const handlePreviewYourNote = () => {
    previewYourNoteClicked();
    setShowPreview(true);
  };

  const handleOnBlur = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    giftWrapMessageEdited({ originalNote, modifiedNote: e.target.value });
  };

  return {
    tCongratulationsCard,
    isGeneratedMessageLoading,
    handlePreviewYourNote,
    handleOnBlur
  };
};
